import { TEST } from "../actions/types";
import testReducer from "../reducers/test";

const DEFAULT_TASK_FLOW_ID = "default";

let taskFlowsQuotaDeleteModalOpen = false;

const cloneDeep = (value) => (typeof structuredClone === "function" ? structuredClone(value) : JSON.parse(JSON.stringify(value)));

const getTaskFlowId = (taskFlow) => {
  if (!taskFlow) return null;
  if (typeof taskFlow === "string" || typeof taskFlow === "number") return String(taskFlow);

  return String(taskFlow.id || taskFlow.task_flow_id || taskFlow.taskFlowId || taskFlow._id || "");
};

const getDeletedTaskFlowIds = (prevTaskFlows = [], nextTaskFlows = []) => {
  const nextTaskFlowIds = new Set(nextTaskFlows.map(getTaskFlowId).filter(Boolean));

  return prevTaskFlows
    .map(getTaskFlowId)
    .filter(Boolean)
    .filter((taskFlowId) => !nextTaskFlowIds.has(taskFlowId));
};

const getQuotasUsingDeletedTaskFlows = (quotas = [], deletedTaskFlowIds = [], prevTaskFlows = []) => {
  const deletedSet = new Set(deletedTaskFlowIds.map(String));

  return quotas
    .map((quota, quotaIndex) => {
      const taskFlowId = quota?.details?.taskFlowId;

      if (!taskFlowId || !deletedSet.has(String(taskFlowId))) return null;

      const taskFlow = prevTaskFlows.find((tf) => String(getTaskFlowId(tf)) === String(taskFlowId));

      return {
        quota,
        quotaIndex,
        taskFlowId,
        taskFlowName: taskFlow?.name || "Deleted Task Flow",
      };
    })
    .filter(Boolean);
};

const moveDeletedTaskFlowQuotasToDefault = (quotas = [], deletedTaskFlowIds = []) => {
  const deletedSet = new Set(deletedTaskFlowIds.map(String));

  return quotas.map((quota) => {
    const taskFlowId = quota?.details?.taskFlowId;

    if (!taskFlowId || !deletedSet.has(String(taskFlowId))) return quota;

    return {
      ...quota,
      details: {
        ...quota.details,
        taskFlowId: DEFAULT_TASK_FLOW_ID,
      },
    };
  });
};

export const TaskFlowsQuotaVerificationMiddleware = (store) => (next) => (action) => {
  if (action.type !== TEST.UPDATE) return next(action);
  if (action.meta?.skipValidation) return next(action);

  const prevTest = store.getState().test;
  const prevDataRef = prevTest?.data;
  const prevScreenerRef = prevTest?.screener;

  const prevTaskFlows = prevDataRef?.taskFlows || [];
  const prevQuotas = prevScreenerRef?.data?.quotas?.quota || [];

  if (!prevTaskFlows.length || !prevQuotas.length) {
    return next(action);
  }

  const nextTestState = testReducer(prevTest, action);
  const nextData = nextTestState?.data || {};
  const nextScreener = nextTestState?.screener || prevScreenerRef;

  const nextTaskFlows = nextData.taskFlows || [];
  const nextQuotas = nextScreener?.data?.quotas?.quota || [];

  const deletedTaskFlowIds = getDeletedTaskFlowIds(prevTaskFlows, nextTaskFlows);

  if (!deletedTaskFlowIds.length) {
    return next(action);
  }

  const affectedQuotas = getQuotasUsingDeletedTaskFlows(nextQuotas, deletedTaskFlowIds, prevTaskFlows);

  if (!affectedQuotas.length) {
    return next(action);
  }

  if (taskFlowsQuotaDeleteModalOpen) return;

  taskFlowsQuotaDeleteModalOpen = true;

  const prevData = cloneDeep(prevDataRef);
  const prevScreener = cloneDeep(prevScreenerRef);

  store.dispatch({
    type: TEST.SHOW_UPDATE_CONFIRMATION_MODAL,
    payload: {
      confirmationFor: TEST.CONFIRMATION_FOR.TASK_FLOWS_QUOTA_DELETE,
      show: true,
      errorMessage: {
        deletedTaskFlowIds,
        affectedQuotas,
      },

      onProceed: () => {
        taskFlowsQuotaDeleteModalOpen = false;

        const updatedQuotas = moveDeletedTaskFlowQuotasToDefault(nextQuotas, deletedTaskFlowIds);

        store.dispatch({
          type: TEST.UPDATE,
          meta: { skipValidation: true },
          payload: {
            data: nextData,
            screener: {
              ...nextScreener,
              data: {
                ...nextScreener?.data,
                quotas: {
                  ...(nextScreener?.data?.quotas || {}),
                  quota: updatedQuotas,
                },
              },
            },
          },
        });
      },

      onCancel: () => {
        taskFlowsQuotaDeleteModalOpen = false;

        store.dispatch({
          type: TEST.UPDATE,
          meta: { skipValidation: true },
          payload: {
            data: prevData,
            screener: prevScreener,
          },
        });
      },
    },
  });

  return;
};
